import { getProgram } from '../../api/src/programs';
import { daysBetween, type IEventView } from './events';
import type { IApplicationView, IClientView, IItemView } from './model';

/** One thing that needs someone at the firm, with the one button that deals with it. */
export interface INeed {
  key: string;
  /** Why it needs you ("Due in 10 days · not yet checked"). */
  why: string;
  title: string;
  detail: string;
  action: string;
  href: string;
  /** New but not urgent (documents a client sent): a grey mark instead of orange. */
  quiet?: boolean;
  /** The application it's about, if any (the board marks its card). */
  applicationKey?: string;
  /** The client it's about (conflict check, ID). */
  clientKey?: string;
}

function shortDate(iso: string): string {
  const d: Date = new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

function inDays(due: string, today: string): string {
  const n: number = daysBetween(today, due);
  return n < 0 ? `Overdue since ${shortDate(due)}` : n === 0 ? 'Due today' : n === 1 ? 'Due tomorrow' : `Due in ${n} days`;
}

/**
 * What needs the firm today, most urgent first: deadlines nobody has checked against the letter yet
 * (and checked ones now overdue), files in Review waiting for a sign-off, clients on an open file
 * without a conflict check or ID verification, and documents clients sent that nobody has looked at.
 */
export function needsYou(d: { apps: IApplicationView[]; events: IEventView[]; clients: IClientView[]; uploaded: IItemView[]; today: string; names: { [key: string]: string } }): INeed[] {
  const open: { [key: string]: IApplicationView } = {};
  for (const a of d.apps.filter(x => x.open)) {
    open[a.key] = a;
  }
  const label = (a: IApplicationView): string => `${d.names[a.clientKey] ?? a.title} · ${getProgram(a.programId)?.short ?? a.programId}`;
  const page = (a: IApplicationView, tab: string): string => `#/applications/${encodeURIComponent(a.key)}/${tab}`;
  const out: INeed[] = [];

  const deadlines: IEventView[] = d.events
    .filter(e => e.kind === 'deadline' && !e.doneOn && !!e.due && !!open[e.applicationKey] && (!e.confirmedOn || e.due < d.today))
    .sort((a, b) => a.due!.localeCompare(b.due!));
  for (const e of deadlines) {
    const a: IApplicationView = open[e.applicationKey];
    out.push(e.confirmedOn
      ? { key: e.key, why: inDays(e.due!, d.today), title: e.title, detail: `${label(a)} · checked by ${e.confirmedBy}`, action: 'Open', href: page(a, 'deadlines'), applicationKey: a.key }
      : { key: e.key, why: `${inDays(e.due!, d.today)} · not yet checked`, title: e.title, detail: `${label(a)} · calculated as ${shortDate(e.due!)}`, action: 'Check the date', href: page(a, 'deadlines'), applicationKey: a.key });
  }

  for (const a of Object.values(open).filter(x => x.stage === 'review')) {
    const signed: IEventView[] = d.events.filter(e => e.applicationKey === a.key && e.kind === 'signoff' && !!e.doneOn);
    const lawyer: IEventView | undefined = signed.filter(e => e.title === 'Lawyer sign-off')[0];
    const clerk: IEventView | undefined = signed.filter(e => e.title === 'Clerk sign-off')[0];
    if (!lawyer) {
      out.push({
        key: `signoff:${a.key}`, why: 'Waiting on a sign-off', title: 'Sign off before submission',
        detail: `${label(a)}${clerk ? ` · clerk signed ${shortDate(clerk.doneOn!.slice(0, 10))}` : ''}`, action: 'Review', href: page(a, 'submission'), applicationKey: a.key
      });
    }
  }

  const onOpenFile: { [clientKey: string]: boolean } = {};
  for (const a of Object.values(open)) {
    onOpenFile[a.clientKey] = true;
  }
  for (const c of d.clients.filter(x => onOpenFile[x.key] && (!x.idOn || !x.conflictOn))) {
    out.push({
      key: `client:${c.key}`, why: 'Before taking on',
      title: !c.conflictOn && !c.idOn ? 'Record the conflict check and verify ID' : !c.idOn ? 'Verify ID' : 'Record the conflict check',
      detail: c.name, action: !c.idOn ? 'Verify ID' : 'Record it', href: `#/clients/${encodeURIComponent(c.key)}`, clientKey: c.key
    });
  }

  const sent: { [key: string]: number } = {};
  for (const i of d.uploaded) {
    const key: string = String(i.rec.fields.ApplicationKey ?? '');
    if (open[key]) {
      sent[key] = (sent[key] ?? 0) + 1;
    }
  }
  for (const key of Object.keys(sent)) {
    const n: number = sent[key];
    out.push({
      key: `sent:${key}`, why: 'New', title: `Check ${n} document${n === 1 ? '' : 's'} the client sent`, detail: label(open[key]),
      action: 'Check', href: `${page(open[key], 'checklist')}?show=check`, quiet: true, applicationKey: key
    });
  }
  return out;
}
